import React, { useState, useEffect } from "react";
import axios from "axios";

const CompanyDetailsForm = ({ onSubmitCompany, barcode }) => {

  const [formData, setFormData] = useState({
    gst_number: "",
    name: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    business_card: null,
  });

  const [preview, setPreview] = useState(null);
  const [gstError, setGstError] = useState("");
  const [gstData, setGstData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  // REGEX validation
  const checkRegex = (value) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
    return gstRegex.test(value);
  };

  // ---------------------- GST Validation Handler ----------------------
  const handleGstNoChange = (e) => {
    let value = e.target.value.toUpperCase();
    if (value.length > 15) return;

    // Position-based validation
    for (let i = 0; i < value.length; i++) {
      const ch = value[i];
      const pos = i + 1;

      if (pos <= 2 && !/[0-9]/.test(ch)) return;
      if (pos > 2 && pos <= 7 && !/[A-Z]/.test(ch)) return;
      if (pos > 7 && pos <= 11 && !/[0-9]/.test(ch)) return;
      if (pos === 12 && !/[A-Z]/.test(ch)) return;
      if (pos === 13 && !/[1-9A-Z]/.test(ch)) return;
      if (pos === 14 && ch !== "Z") return;
      if (pos === 15 && !/[0-9A-Z]/.test(ch)) return;
    }

    setFormData((prev) => ({ ...prev, gst_number: value }));

    if (value.length < 15) {
      setGstError("");
      setGstData(null);
    }
  };

  // ---------------------- useEffect GST API Trigger ----------------------
  useEffect(() => {
    const gst = formData.gst_number;

    if (!gst || gst.length !== 15 || !checkRegex(gst)) return;

    setLoading(true); // 🔥 start loader

    axios
      .get(`${process.env.REACT_APP_API_END_POINT}/check-gst-validation/${gst}`)
      .then((res) => {
        if (res.data.flag === false) {
          setGstError(res.data.message);
          return;
        }

        setGstError("");
        const d = res.data;
        setGstData(d);

        setFormData((prev) => ({
          ...prev,
          name: d.company || "",
          address: d.address || "",
          city: d.city || "",
          state: d.state || "",
          pincode: d.postalCode || "",
        }));
      })
      .catch(() => setGstError("Failed to validate GST"))
      .finally(() => {
        setLoading(false); // ✅ stop loader
      });
  }, [formData.gst_number]);

  // ---------------------- Other Input Handler ----------------------
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "business_card") {
      const file = files[0];
      setFormData({ ...formData, business_card: file });
      if (file) setPreview(URL.createObjectURL(file));
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  // ---------------------- Submit ----------------------
  const submitForm = async () => {
    if (gstError) {
      alert("Fix GST number before submitting");
      return;
    }

    setSubmitLoading(true); // 🔥 start submit loader

    try {
      await onSubmitCompany(formData); // assuming promise
    } catch (err) {
      alert("Failed to submit company details");
    } finally {
      setSubmitLoading(false); // ✅ stop loader
    }
  };



  // ---------------------- UI ----------------------
  return (
    <div style={styles.wrapper}>
      <h2 style={styles.title}>Company Details ({barcode})</h2>

      <div style={styles.form}>
        
        {/* GST */}
        <label>GST Number</label>
        <input
          type="text"
          name="gst_number"
          value={formData.gst_number}
          onChange={handleGstNoChange}
          placeholder="Enter GST Number"
          style={styles.input}
        />
        {gstError && <p style={styles.error}>{gstError}</p>}

        {/* Company Name */}
        <label>Company Name</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          placeholder="Enter Company Name"
          style={styles.input}
        />

        {/* Address */}
        <label>Address</label>
        <textarea
          name="address"
          value={formData.address}
          onChange={handleChange}
          placeholder="Enter Address"
          style={styles.textarea}
        />

        {/* City */}
        <label>City</label>
        <input
          type="text"
          name="city"
          value={formData.city}
          onChange={handleChange}
          placeholder="Enter City"
          style={styles.input}
        />

        {/* State */}
        <label>State</label>
        <input
          type="text"
          name="state"
          value={formData.state}
          onChange={handleChange}
          placeholder="Enter State"
          style={styles.input}
        />

        {/* Pincode */}
        <label>Pincode</label>
        <input
          type="text"
          name="pincode"
          maxLength={6}
          value={formData.pincode}
          onChange={handleChange}
          placeholder="Enter Pincode"
          style={styles.input}
        />

        {/* Business Card */}
        <label>Upload Business Card</label>
        <input
          type="file"
          name="business_card"
          accept="image/*"
          onChange={handleChange}
          style={styles.fileInput}
        />

        {preview && (
          <div style={styles.previewBox}>
            <img src={preview} alt="Business Card" style={styles.previewImg} />
          </div>
        )}

        <button
          onClick={submitForm}
          disabled={submitLoading || loading}
          style={{
            ...styles.button,
            opacity: submitLoading || loading ? 0.7 : 1,
            cursor: submitLoading || loading ? "not-allowed" : "pointer",
          }}
        >
          {submitLoading ? "Submitting..." : loading ? "Validating GST..." : "Submit"}
        </button>


      </div>
    </div>
  );
};

export default CompanyDetailsForm;

const styles = {
  wrapper: {
    maxWidth: "550px",
    margin: "auto",
    padding: "20px",
    fontFamily: "Arial",
  },
  title: {
    textAlign: "center",
    marginBottom: "20px",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  input: {
    padding: "10px",
    borderRadius: "5px",
    border: "1px solid #ccc",
  },
  textarea: {
    padding: "10px",
    minHeight: "70px",
    borderRadius: "5px",
    border: "1px solid #ccc",
  },
  fileInput: {
    padding: "8px 0",
  },
  previewBox: {
    marginTop: "10px",
    textAlign: "center",
  },
  previewImg: {
    width: "100%",
    maxHeight: "200px",
    objectFit: "contain",
    borderRadius: "8px",
    border: "1px solid #ccc",
  },
  error: {
    color: "red",
    fontSize: "13px",
  },
  button: {
    marginTop: "15px",
    padding: "12px",
    background: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
  },
};
